<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="service.isDiff"
    :isOutdated="isOutdated"
    :item="service"
    :itemType="'CHAT_SERVICE'"
    :typename="'service'"
    :onUpdate="updateService"
    :onCreate="createService"
    :onDelete="deleteService"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
  >
    <fieldset slot="fieldset" :disabled="isOutdated || service.isDiff">
      <div class="two fields">
        <div
          class="field required"
          :class="{ error: $v.service.name.$error }"
          v-diff-field="serviceDiff.name"
        >
          <label>{{ $t('SERVICE_NAME') }}</label>
          <input
            type="text"
            name="serviceName"
            :placeholder="$t('SERVICE_NAME_PLACEHOLDER')"
            v-model="service.name"
            @click="$v.service.name.$touch()"
            :disabled="isOutdated || service.isDiff"
          />
        </div>
        <div
          class="field required"
          :class="{ error: $v.service.key.$error }"
          v-diff-field="serviceDiff.key"
        >
          <label>{{ $t('SERVICE_KEY') }}</label>
          <input
            type="text"
            name="serviceKey"
            :placeholder="$t('SERVICE_KEY_PLACEHOLDER')"
            v-model="service.key"
            @click="$v.service.key.$touch()"
            :disabled="isOutdated || service.isDiff"
          />
        </div>
      </div>

      <div class="inline fields">
        <!-- CHAT SERVICE TYPE -->
        <label>{{ $tc('CHAT_SERVICE_TYPE', 1) }}</label>
        <div
          class="field"
          v-for="(typeName, capitalized) in chatTypes"
          :key="typeName"
          v-diff-field.disable="serviceDiff.serviceConfiguration.type"
        >
          <div class="ui radio checkbox">
            <input
              type="radio"
              name="chatServiceType"
              :id="'chatServiceType' + typeName"
              :value="typeName"
              v-model="service.serviceConfiguration.type"
            />
            <label :for="'chatServiceType' + typeName">
              {{ $tc('CHAT_SERVICE_TYPE_' + capitalized, 1) }}
            </label>
          </div>
        </div>
      </div>

      <template>
        <h4 class="ui horizontal left aligned divider header">{{ $t('SLACK') }}</h4>
        <div class="ui basic segment">
          <div
            class="sixten wide field required"
            :class="{ error: $v.service.serviceConfiguration.appToken.$error }"
            v-diff-field="serviceDiff.serviceConfiguration.appToken"
          >
            <label>{{ $t('SLACK_APP_TOKEN') }}</label>
            <input
              type="text"
              name="appToken"
              :placeholder="$t('SLACK_APP_TOKEN_PLACEHOLDER')"
              v-model="service.serviceConfiguration.appToken"
              @click="$v.service.serviceConfiguration.appToken.$touch()"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
          <div class="ui info message" v-html="$t('SLACK_APP_TOKEN_HELP')"></div>
          <div
            class="sixten wide field required"
            :class="{ error: $v.service.serviceConfiguration.botUserToken.$error }"
            v-diff-field="serviceDiff.serviceConfiguration.botUserToken"
          >
            <label>{{ $t('SLACK_BOT_USER_TOKEN') }}</label>
            <input
              type="text"
              name="botUserToken"
              :placeholder="$t('SLACK_BOT_USER_TOKEN_PLACEHOLDER')"
              v-model="service.serviceConfiguration.botUserToken"
              @click="$v.service.serviceConfiguration.botUserToken.$touch()"
              :disabled="isOutdated || service.isDiff"
            />
          </div>
          <div class="ui info message" v-html="$t('SLACK_BOT_USER_TOKEN_HELP')"></div>
        </div>
      </template>
      <!-- Used in ... -->
      <template v-if="mode === 'edit'">
        <h4 class="ui horizontal left aligned divider header">
          {{ $t('USED_IN', { thing: $tc('FLOW', (usedIn && usedIn.length) || 0) }) }}
        </h4>
        <div class="ui basic segment">
          <div class="field" v-if="usedIn && usedIn.length > 0">
            <div class="ui link list" id="usedIn">
              <router-link
                class="item"
                :to="{ name: u.routeName, params: u.routeParams }"
                :key="u.id"
                v-for="u in usedIn"
              >
                <span>
                  <i class="share icon" v-bind:class="{ hidden: u.as === 'trigger' }" />
                  <span class="content">{{ u.name }}</span>
                </span>
              </router-link>
            </div>
          </div>
          <div v-else>
            <span class="text bold">{{ $t('SERVICE_NOT_USED') }}</span>
          </div>
        </div>
      </template>
    </fieldset>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
</style>

<script>
// @flow
import Vue from 'vue'
import _ from 'lodash'
import { Service, Flow, Toast, Typenames, ErrorCodes } from 'services'
import { required, numeric, helpers, maxValue, and, or } from 'vuelidate/lib/validators'
import { MainSection } from 'ui'
import { map } from 'rxjs/operators'
import { ActionTypes } from 'store'

const ChatType = {
  SLACK: 'slack'
}

const blankServiceConfiguration = {
  type: ChatType.SLACK,
  typeIcon: 'slack'
}

const validAppToken = helpers.regex('appToken', /^xapp-.+$/)
const validBotUserToken = helpers.regex('botUserToken', /^xoxb-.+$/)

export default {
  name: 'chat-service',
  props: ['serviceId'],
  components: { MainSection },

  data() {
    return {
      service: {
        serviceType: 'CHAT',
        serviceConfiguration: _.clone(blankServiceConfiguration)
      },
      serviceDiff: { serviceConfiguration: {} },
      isOutdated: false,
      chatTypes: ChatType
    }
  },

  validations: {
    service: {
      name: { required },
      key: { required },
      serviceConfiguration: {
        appToken: { valid: and(required, validAppToken) },
        botUserToken: { valid: and(required, validBotUserToken) }
      }
    }
  },

  computed: {
    allFlows() {
      return this.$store.state.flow.items
    },
    mode() {
      return this.service && this.service.id ? 'edit' : 'create'
    },

    usedIn() {
      return _.map(
        _.filter(
          this.allFlows,
          f =>
            f.typeConfig &&
            f.typeConfig.triggers &&
            f.typeConfig.triggers.services &&
            _.find(
              f.typeConfig.triggers.services,
              s =>
                s.serviceType === 'CHAT' &&
                this.service &&
                s.configuration.serviceId === this.service.id
            )
        ),
        f => {
          return {
            as: 'trigger',
            name: f.name,
            routeName: 'app-flow',
            routeParams: {
              appId: f.application,
              flowId: f.id,
              mode: 'settings'
            }
          }
        }
      )
    },
    isEditMode() {
      return this.mode === 'edit'
    }
  },

  methods: {
    updateService() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return Service.updateService(this.serviceId, this.service)
            .then(res => {
              this.service.version = res.version
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('SERVICE', 1) }), {
                remove: 5000
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) }),
                    thing: this.$i18n.tc('SERVICE', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('USER', 1) }),
              thing: this.$i18n.tc('USER', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('SERVICE', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    createService() {
      if (!this.$v.$invalid) {
        console.debug('creating service', this.service)
        return Service.createService(this.service)
          .then(res => {
            this.service.id = res.id
            this.service.version = res.version
          })
          .then(() => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('SERVICE', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'service',
              params: { serviceId: this.service.id, serviceType: 'CHAT' }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SERVICE', 1) })
              }),
              { remove: 5000 }
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('SERVICE', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteService() {
      Service.deleteService(this.service.id)
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('SERVICE', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'services'
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('SERVICE', 1) })
            })
          )
          console.error(err)
        })
    },

    readService(id) {
      return Service.readService(id)
        .then(s => {
          this.service = _.merge(
            { serviceConfiguration: _.clone(blankServiceConfiguration) },
            s || {}
          )
          this.isOutdated = false
          Vue.nextTick(this.$v.$touch)
          return this.service
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('SERVICE', 1) })
            })
          )
          console.error(err)
        })
    },

    editMode() {
      this.$v.$reset()
      this.readService(this.$route.params.serviceId)
    },

    createMode() {
      this.$v.$reset()
      this.service = {
        serviceType: 'CHAT',
        serviceConfiguration: _.clone(blankServiceConfiguration)
      }
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.service = changedItem
      this.serviceDiff = !_.isEmpty(fullDiff) ? fullDiff : { serviceConfiguration: {} }
    },
    onRevert(service) {
      this.service = service
      this.serviceDiff = { serviceConfiguration: {} }
    }
  },
  created() {
    if (this.$route.params.serviceId) {
      this.$v.$reset()
      this.readService(this.$route.params.serviceId).then(() => {})
    }
    Flow.readAllFlows()
  },

  watch: {
    '$route.params.serviceId': function(newServiceId) {
      if (newServiceId) {
        this.editMode()
      } else {
        this.createMode()
      }
    }
  }
}
</script>
